import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

/**
 * FeeRecoveryPage - Fee Recovery Tracking Dashboard
 * Identify and recover fees from candidates who may have been hired directly
 */

const API_BASE = process.env.REACT_APP_API_URL || '';

const api = async (endpoint, options = {}) => {
  const token = localStorage.getItem('token');
  const res = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...options.headers
    }
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || `API ${res.status}`);
  }
  return res.json();
};

// Formatters
const fmt = {
  money: (n) => !n ? '$0' : n >= 1e6 ? `$${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n).toLocaleString()}`,
  moneyFull: (n) => n ? `$${Math.round(n).toLocaleString()}` : '$0',
  num: (n) => !n ? '0' : n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `${(n/1e3).toFixed(1)}k` : String(n),
  date: (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—',
  pct: (n) => `${(n || 0).toFixed(1)}%`,
};

// Theme hook
const useTheme = () => {
  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('kubi_darkMode');
    if (stored !== null) return stored !== 'false';
    return true;
  });

  useEffect(() => {
    const handleStorage = () => {
      const stored = localStorage.getItem('kubi_darkMode');
      setIsDark(stored !== 'false');
    };
    window.addEventListener('storage', handleStorage);
    const interval = setInterval(handleStorage, 500);
    return () => { window.removeEventListener('storage', handleStorage); clearInterval(interval); };
  }, []);

  return { isDark };
};

// Status configurations
const STATUS_CONFIG = {
  pending: { label: 'Pending', color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)' },
  verified: { label: 'Verified', color: '#1E88E5', bg: 'rgba(30, 136, 229, 0.1)' },
  recovered: { label: 'Recovered', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.1)' },
  lost: { label: 'Lost', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)' },
  disputed: { label: 'Disputed', color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' }
};

const VERIFICATION_STATUS = {
  needs_check: { label: 'Needs Check', color: '#f59e0b' },
  confirmed_hired: { label: 'Confirmed Hired', color: '#22c55e' },
  not_hired: { label: 'Not Hired', color: '#ef4444' },
  unknown: { label: 'Unknown', color: '#64748b' }
};

// Position status options for filtering
const POSITION_STATUS_OPTIONS = [
  { value: 'all', label: 'All Positions', color: '#1565C0' },
  { value: 'open', label: 'Open', color: '#22c55e' },
  { value: 'closed', label: 'Closed', color: '#64748b' },
  { value: 'filled', label: 'Filled', color: '#0a66c2' },
  { value: 'lost', label: 'Lost', color: '#ef4444' },
];

// Activity stages - submit or beyond
const ACTIVITY_STAGES = [
  { value: 'all', label: 'All Activity' },
  { value: 'submit', label: 'Submit' },
  { value: 'interview', label: 'Interview' },
  { value: 'offer', label: 'Offer' },
  { value: 'placement', label: 'Placement' },
];

// CSS Animations
const styles = `
@keyframes fr-fadeIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
@keyframes fr-slideIn {
  from { opacity: 0; transform: translateX(-20px); }
  to { opacity: 1; transform: translateX(0); }
}
@keyframes fr-scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}
@keyframes fr-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}
@keyframes fr-shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}
@keyframes fr-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
@keyframes fr-countUp {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.fr-card-animate { animation: fr-fadeIn 0.4s ease-out forwards; }
.fr-slide-animate { animation: fr-slideIn 0.3s ease-out forwards; }
.fr-scale-animate { animation: fr-scaleIn 0.3s ease-out forwards; }
.fr-hover-lift { transition: transform 0.2s, box-shadow 0.2s; }
.fr-hover-lift:hover { transform: translateY(-2px); box-shadow: 0 8px 30px rgba(0,0,0,0.12); }
.fr-spin { animation: fr-spin 1s linear infinite; }
.fr-pulse { animation: fr-pulse 2s ease-in-out infinite; }
.fr-count-animate { animation: fr-countUp 0.5s ease-out forwards; }
.fr-progress-bar {
  background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
  background-size: 200% 100%;
  animation: fr-shimmer 1.5s linear infinite;
}
`;

// Icons as inline SVG components
const Icons = {
  DollarSign: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>,
  TrendingUp: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>,
  Target: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>,
  Users: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
  Search: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  RefreshCw: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/></svg>,
  CheckCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
  XCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>,
  AlertCircle: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>,
  Clock: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  Linkedin: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"/><rect x="2" y="9" width="4" height="12"/><circle cx="4" cy="4" r="2"/></svg>,
  ExternalLink: () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>,
  ChevronDown: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"/></svg>,
  Filter: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/></svg>,
  Eye: () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>,
  MoreHorizontal: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"/><circle cx="19" cy="12" r="1"/><circle cx="5" cy="12" r="1"/></svg>,
  X: () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>,
  Award: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="8" r="7"/><polyline points="8.21 13.89 7 23 12 20 17 23 15.79 13.88"/></svg>,
  BarChart: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="20" x2="12" y2="10"/><line x1="18" y1="20" x2="18" y2="4"/><line x1="6" y1="20" x2="6" y2="16"/></svg>,
  Loader: () => <svg className="fr-spin" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>,
};

// Animated Counter Component
const AnimatedCounter = ({ value, prefix = '', suffix = '', duration = 1000 }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const end = parseFloat(value) || 0;
    const increment = end / (duration / 16);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) {
        setDisplayValue(end);
        clearInterval(timer);
      } else {
        setDisplayValue(Math.floor(start));
      }
    }, 16);
    return () => clearInterval(timer);
  }, [value, duration]);

  return <span className="fr-count-animate">{prefix}{displayValue.toLocaleString()}{suffix}</span>;
};

// KPI Card Component
const KPICard = ({ icon: Icon, label, value, sub, color = '#1565C0', isDark, animationDelay = 0, highlight = false }) => (
  <div
    className="fr-card-animate fr-hover-lift"
    style={{
      background: highlight
        ? `linear-gradient(135deg, ${color}, ${color}dd)`
        : (isDark ? '#1e293b' : '#fff'),
      borderRadius: 16,
      padding: '20px 24px',
      border: highlight ? 'none' : `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
      animationDelay: `${animationDelay}ms`,
      color: highlight ? '#fff' : 'inherit'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <div style={{
          fontSize: 12,
          color: highlight ? 'rgba(255,255,255,0.8)' : '#64748b',
          marginBottom: 8,
          textTransform: 'uppercase',
          fontWeight: 600,
          letterSpacing: '0.5px'
        }}>{label}</div>
        <div style={{ fontSize: 32, fontWeight: 800 }}>{value}</div>
        {sub && <div style={{ fontSize: 13, color: highlight ? 'rgba(255,255,255,0.7)' : '#64748b', marginTop: 4 }}>{sub}</div>}
      </div>
      <div style={{
        width: 48, height: 48, borderRadius: 12,
        background: highlight ? 'rgba(255,255,255,0.2)' : `${color}15`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: highlight ? '#fff' : color
      }}>
        <Icon />
      </div>
    </div>
  </div>
);

// Progress Bar Component
const ProgressBar = ({ value, max, color = '#22c55e', label, isDark }) => {
  const pct = max > 0 ? (value / max) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 13, color: '#64748b' }}>{label}</span>
        <span style={{ fontSize: 13, fontWeight: 600, color }}>{fmt.pct(pct)}</span>
      </div>
      <div style={{
        height: 8,
        background: isDark ? '#334155' : '#e5e7eb',
        borderRadius: 4,
        overflow: 'hidden'
      }}>
        <div style={{
          height: '100%',
          width: `${pct}%`,
          background: `linear-gradient(90deg, ${color}, ${color}cc)`,
          borderRadius: 4,
          transition: 'width 0.8s ease-out'
        }} />
      </div>
    </div>
  );
};

// Opportunity Card Component
const OpportunityCard = ({ opportunity, isDark, onAction, animationDelay = 0 }) => {
  const [expanded, setExpanded] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const status = STATUS_CONFIG[opportunity.status] || STATUS_CONFIG.pending;
  const verification = VERIFICATION_STATUS[opportunity.verificationStatus] || VERIFICATION_STATUS.needs_check;

  return (
    <div
      className="fr-card-animate fr-hover-lift"
      style={{
        background: isDark ? '#1e293b' : '#fff',
        borderRadius: 16,
        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        overflow: 'hidden',
        animationDelay: `${animationDelay}ms`
      }}
    >
      {/* Card Header */}
      <div
        style={{
          padding: '16px 20px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: 16
        }}
        onClick={() => setExpanded(!expanded)}
      >
        {/* Avatar */}
        <div style={{
          width: 48, height: 48, borderRadius: 12,
          background: `linear-gradient(135deg, #1565C0, #1E88E5)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#fff', fontWeight: 700, fontSize: 16, flexShrink: 0
        }}>
          {(opportunity.candidateName || 'U')[0].toUpperCase()}
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>
            {opportunity.candidateName || 'Unknown Candidate'}
          </div>
          <div style={{ fontSize: 13, color: '#64748b', display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>{opportunity.positionTitle || 'Unknown Position'}</span>
            <span>•</span>
            <span>{opportunity.companyName || 'Unknown Company'}</span>
          </div>
        </div>

        {/* Fee */}
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#22c55e' }}>
            {fmt.money(opportunity.estimatedFeeMax)}
          </div>
          <div style={{ fontSize: 11, color: '#64748b' }}>
            Est. {opportunity.feePercentage || 20}% fee
          </div>
        </div>

        {/* Status Badges */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end', flexShrink: 0 }}>
          <span style={{
            padding: '4px 10px', borderRadius: 8,
            background: status.bg, color: status.color,
            fontSize: 11, fontWeight: 600
          }}>
            {status.label}
          </span>
          <span style={{
            padding: '2px 8px', borderRadius: 6,
            background: `${verification.color}15`, color: verification.color,
            fontSize: 10, fontWeight: 500
          }}>
            {verification.label}
          </span>
        </div>

        {/* Expand Icon */}
        <Icons.ChevronDown style={{
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.2s',
          color: '#64748b',
          flexShrink: 0
        }} />
      </div>

      {/* Expanded Content */}
      {expanded && (
        <div style={{
          borderTop: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          padding: 20,
          background: isDark ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)'
        }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 20, marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Submitted</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>{fmt.date(opportunity.submittedAt)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Salary Range</div>
              <div style={{ fontSize: 14, fontWeight: 500 }}>
                {fmt.moneyFull(opportunity.salaryMin)} - {fmt.moneyFull(opportunity.salaryMax)}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Fee Range</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: '#22c55e' }}>
                {fmt.moneyFull(opportunity.estimatedFeeMin)} - {fmt.moneyFull(opportunity.estimatedFeeMax)}
              </div>
            </div>
            {opportunity.currentEmployer && (
              <div>
                <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Current Employer</div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{opportunity.currentEmployer}</div>
              </div>
            )}
          </div>

          {opportunity.notes && (
            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 11, color: '#64748b', marginBottom: 4, textTransform: 'uppercase' }}>Notes</div>
              <div style={{
                padding: 12,
                background: isDark ? '#0f172a' : '#f8fafc',
                borderRadius: 8,
                fontSize: 13,
                whiteSpace: 'pre-wrap'
              }}>
                {opportunity.notes}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {opportunity.status === 'pending' && (
              <>
                <button
                  onClick={() => onAction(opportunity.id, 'verify')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 10,
                    background: 'linear-gradient(135deg, #1E88E5, #1565C0)',
                    border: 'none', color: '#fff',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <Icons.Linkedin /> Verify on LinkedIn
                </button>
                <button
                  onClick={() => onAction(opportunity.id, 'confirmed_hired')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 10,
                    background: 'rgba(34, 197, 94, 0.15)',
                    border: '1px solid rgba(34, 197, 94, 0.3)',
                    color: '#22c55e',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <Icons.CheckCircle /> Mark Verified
                </button>
                <button
                  onClick={() => onAction(opportunity.id, 'lost')}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    padding: '10px 16px', borderRadius: 10,
                    background: isDark ? '#334155' : '#f1f5f9',
                    border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
                    color: '#64748b',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  <Icons.XCircle /> Mark Lost
                </button>
              </>
            )}
            {opportunity.status === 'verified' && (
              <button
                onClick={() => onAction(opportunity.id, 'recovered')}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6,
                  padding: '10px 16px', borderRadius: 10,
                  background: 'linear-gradient(135deg, #22c55e, #10b981)',
                  border: 'none', color: '#fff',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer'
                }}
              >
                <Icons.DollarSign /> Record Recovery
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Recovery Modal Component
const RecoveryModal = ({ opportunity, isDark, onClose, onSubmit }) => {
  const [amount, setAmount] = useState(opportunity?.estimatedFeeMax || 0);
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      await onSubmit(opportunity.id, { status: 'recovered', recoveredAmount: amount, notes });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 1000
    }} onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div
        className="fr-scale-animate kubi-modal-card"
        style={{
          background: isDark ? '#1e293b' : '#fff',
          borderRadius: 20,
          width: '100%',
          maxWidth: 480,
          overflow: 'hidden'
        }}
      >
        <div style={{
          padding: '20px 24px',
          borderBottom: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between'
        }}>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Record Fee Recovery</h3>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 8,
              border: 'none', background: isDark ? '#334155' : '#f1f5f9',
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#64748b'
            }}
          >
            <Icons.X />
          </button>
        </div>
        <div style={{ padding: 24 }}>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Recovered Amount
            </label>
            <div style={{ position: 'relative' }}>
              <span style={{
                position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)',
                color: '#64748b', fontWeight: 600
              }}>$</span>
              <input
                type="number"
                value={amount}
                onChange={e => setAmount(parseFloat(e.target.value) || 0)}
                style={{
                  width: '100%',
                  padding: '12px 14px 12px 28px',
                  borderRadius: 10,
                  border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                  background: isDark ? '#0f172a' : '#fff',
                  color: isDark ? '#f8fafc' : '#1f2937',
                  fontSize: 18,
                  fontWeight: 600
                }}
              />
            </div>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>
              Estimated: {fmt.moneyFull(opportunity?.estimatedFeeMin)} - {fmt.moneyFull(opportunity?.estimatedFeeMax)}
            </div>
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="Add any notes about this recovery..."
              style={{
                width: '100%',
                padding: 12,
                borderRadius: 10,
                border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                background: isDark ? '#0f172a' : '#fff',
                color: isDark ? '#f8fafc' : '#1f2937',
                fontSize: 14,
                minHeight: 80,
                resize: 'vertical'
              }}
            />
          </div>
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', marginBottom: 16 }}>
              <div style={{ width: 24, height: 24, borderRadius: 6, background: 'linear-gradient(135deg, #ef4444, #1565C0)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 10, fontWeight: 700, flexShrink: 0 }}>K9</div>
              <span style={{ fontSize: 13, color: '#ef4444' }}>{error}</span>
            </div>
          )}
          <div style={{ display: 'flex', gap: 12 }}>
            <button
              onClick={onClose}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10,
                border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                background: 'transparent',
                color: isDark ? '#f8fafc' : '#1f2937',
                fontSize: 14, fontWeight: 600, cursor: 'pointer'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={loading || amount <= 0}
              style={{
                flex: 1, padding: '12px 16px', borderRadius: 10,
                border: 'none',
                background: 'linear-gradient(135deg, #22c55e, #10b981)',
                color: '#fff',
                fontSize: 14, fontWeight: 600, cursor: 'pointer',
                opacity: loading || amount <= 0 ? 0.7 : 1
              }}
            >
              {loading ? 'Recording...' : `Record ${fmt.money(amount)}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Calendar Icon
const CalendarIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const SlidersIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="21" x2="4" y2="14"/><line x1="4" y1="10" x2="4" y2="3"/><line x1="12" y1="21" x2="12" y2="12"/><line x1="12" y1="8" x2="12" y2="3"/><line x1="20" y1="21" x2="20" y2="16"/><line x1="20" y1="12" x2="20" y2="3"/><line x1="1" y1="14" x2="7" y2="14"/><line x1="9" y1="8" x2="15" y2="8"/><line x1="17" y1="16" x2="23" y2="16"/></svg>;

// Main FeeRecoveryPage Component
export default function FeeRecoveryPage() {
  const { isDark } = useTheme();
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [opportunities, setOpportunities] = useState([]);
  const [summary, setSummary] = useState({});
  const [analytics, setAnalytics] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [recoveryModal, setRecoveryModal] = useState(null);

  // kNine branded modal (replaces native alert)
  const [knModal, setKnModal] = useState(null);
  const knModalResolveRef = useRef(null);
  const knAlert = (message, opts = {}) => new Promise(resolve => {
    knModalResolveRef.current = resolve;
    setKnModal({ type: 'alert', message, title: opts.title || 'kNine', variant: opts.variant || 'info', ...opts });
  });
  const handleKnModalClose = (result) => {
    const resolve = knModalResolveRef.current;
    knModalResolveRef.current = null;
    setKnModal(null);
    if (resolve) resolve(result);
  };

  // Advanced filters
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [positionStatus, setPositionStatus] = useState('all');
  const [activityStage, setActivityStage] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [oppsRes, analyticsRes] = await Promise.all([
        api('/fee-recovery/opportunities'),
        api('/fee-recovery/analytics')
      ]);
      setOpportunities(oppsRes.opportunities || []);
      setSummary(oppsRes.summary || {});
      setAnalytics(analyticsRes);
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const result = await api('/fee-recovery/sync', { method: 'POST' });
      await knAlert(result.message || 'Sync complete', { title: 'Sync Complete', variant: 'success' });
      loadData();
    } catch (err) {
      await knAlert('Sync failed: ' + err.message, { title: 'Sync Error', variant: 'error' });
    } finally {
      setSyncing(false);
    }
  };

  const handleAction = async (id, action) => {
    if (action === 'recovered') {
      const opp = opportunities.find(o => o.id === id);
      setRecoveryModal(opp);
      return;
    }

    try {
      if (action === 'verify') {
        // Open LinkedIn search
        const opp = opportunities.find(o => o.id === id);
        const searchQuery = encodeURIComponent(`${opp.candidateName} ${opp.companyName}`);
        window.open(`https://www.linkedin.com/search/results/people/?keywords=${searchQuery}`, '_blank');
        return;
      }

      if (action === 'confirmed_hired') {
        await api(`/fee-recovery/opportunities/${id}/verify`, {
          method: 'POST',
          body: JSON.stringify({ verificationStatus: 'confirmed_hired' })
        });
      } else if (action === 'lost') {
        await api(`/fee-recovery/opportunities/${id}`, {
          method: 'PUT',
          body: JSON.stringify({ status: 'lost' })
        });
      }

      loadData();
    } catch (err) {
      await knAlert('Action failed: ' + err.message, { title: 'Error', variant: 'error' });
    }
  };

  const handleRecoverySubmit = async (id, data) => {
    await api(`/fee-recovery/opportunities/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data)
    });
    loadData();
  };

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter opportunities
  const filteredOpportunities = useMemo(() => {
    let filtered = opportunities;

    // Status filter
    if (filter !== 'all') {
      filtered = filtered.filter(o => o.status === filter);
    }

    // Position status filter
    if (positionStatus !== 'all') {
      filtered = filtered.filter(o => (o.positionStatus || '').toLowerCase() === positionStatus);
    }

    // Activity stage filter (submit or beyond)
    if (activityStage !== 'all') {
      const stageOrder = ['submit', 'interview', 'offer', 'placement'];
      const minStageIdx = stageOrder.indexOf(activityStage);
      if (minStageIdx >= 0) {
        filtered = filtered.filter(o => {
          const oppStageIdx = stageOrder.indexOf((o.lastActivityStage || '').toLowerCase());
          return oppStageIdx >= minStageIdx;
        });
      }
    }

    // Date range filter
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      filtered = filtered.filter(o => {
        const activityDate = o.lastActivityAt || o.submittedAt;
        return activityDate && new Date(activityDate) >= fromDate;
      });
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(o => {
        const activityDate = o.lastActivityAt || o.submittedAt;
        return activityDate && new Date(activityDate) <= toDate;
      });
    }

    // Search query
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter(o =>
        (o.candidateName || '').toLowerCase().includes(q) ||
        (o.positionTitle || '').toLowerCase().includes(q) ||
        (o.companyName || '').toLowerCase().includes(q)
      );
    }

    return filtered;
  }, [opportunities, filter, searchQuery, positionStatus, activityStage, dateFrom, dateTo]);

  // Clear all advanced filters
  const clearAdvancedFilters = () => {
    setPositionStatus('all');
    setActivityStage('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveAdvancedFilters = positionStatus !== 'all' || activityStage !== 'all' || dateFrom || dateTo;

  // Inject styles
  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  if (loading && opportunities.length === 0) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 16 }}>
        <Icons.Loader />
        <div style={{ color: '#64748b' }}>Loading fee recovery data...</div>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div className="kubi-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, gap: 12 }}>
        <div>
          <h1 className="kubi-page-title" style={{ margin: '0 0 4px', fontSize: 28, fontWeight: 800 }}>Fee Recovery</h1>
          <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>Identify and recover fees from direct hires</p>
        </div>
        <button
          onClick={handleSync}
          disabled={syncing}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '10px 16px',
            background: isDark ? '#334155' : '#f1f5f9',
            border: `1px solid ${isDark ? '#475569' : '#e5e7eb'}`,
            borderRadius: 10,
            color: isDark ? '#f8fafc' : '#1f2937',
            cursor: syncing ? 'not-allowed' : 'pointer',
            fontSize: 14,
            fontWeight: 600,
            opacity: syncing ? 0.7 : 1
          }}
        >
          <Icons.RefreshCw style={{ animation: syncing ? 'fr-spin 1s linear infinite' : 'none' }} />
          {syncing ? 'Scanning...' : 'Scan for Opportunities'}
        </button>
      </div>

      {/* KPIs */}
      <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, marginBottom: 24 }}>
        <KPICard
          icon={Icons.Target}
          label="Total Opportunities"
          value={<AnimatedCounter value={summary.pending + summary.verified || 0} />}
          sub={`${summary.pending || 0} pending verification`}
          color="#1565C0"
          isDark={isDark}
          animationDelay={0}
        />
        <KPICard
          icon={Icons.DollarSign}
          label="Potential Recovery"
          value={<AnimatedCounter value={summary.totalPotential || 0} prefix="$" />}
          sub="From all opportunities"
          color="#f59e0b"
          isDark={isDark}
          animationDelay={100}
        />
        <KPICard
          icon={Icons.Award}
          label="Total Recovered"
          value={<AnimatedCounter value={summary.totalRecovered || 0} prefix="$" />}
          sub={`${summary.recovered || 0} successful recoveries`}
          color="#22c55e"
          isDark={isDark}
          animationDelay={200}
          highlight
        />
        <KPICard
          icon={Icons.TrendingUp}
          label="Recovery Rate"
          value={analytics?.summary?.recoveryRate ? `${analytics.summary.recoveryRate}%` : '0%'}
          sub="Of verified opportunities"
          color="#1E88E5"
          isDark={isDark}
          animationDelay={300}
        />
      </div>

      {/* Progress Overview */}
      <div style={{
        background: isDark ? '#1e293b' : '#fff',
        borderRadius: 16,
        border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
        padding: 20,
        marginBottom: 24
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <Icons.BarChart />
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Recovery Pipeline</h3>
        </div>
        <div className="kubi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
          <ProgressBar value={summary.pending || 0} max={(summary.pending || 0) + (summary.verified || 0) + (summary.recovered || 0) + (summary.lost || 0) || 1} color="#f59e0b" label={`Pending (${summary.pending || 0})`} isDark={isDark} />
          <ProgressBar value={summary.verified || 0} max={(summary.pending || 0) + (summary.verified || 0) + (summary.recovered || 0) + (summary.lost || 0) || 1} color="#1E88E5" label={`Verified (${summary.verified || 0})`} isDark={isDark} />
          <ProgressBar value={summary.recovered || 0} max={(summary.pending || 0) + (summary.verified || 0) + (summary.recovered || 0) + (summary.lost || 0) || 1} color="#22c55e" label={`Recovered (${summary.recovered || 0})`} isDark={isDark} />
          <ProgressBar value={summary.lost || 0} max={(summary.pending || 0) + (summary.verified || 0) + (summary.recovered || 0) + (summary.lost || 0) || 1} color="#ef4444" label={`Lost (${summary.lost || 0})`} isDark={isDark} />
        </div>
      </div>

      {/* Filter & Search */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div style={{ display: 'flex', gap: 4, background: isDark ? '#1e293b' : '#f1f5f9', padding: 4, borderRadius: 10 }}>
            {[
              { id: 'all', label: 'All' },
              { id: 'pending', label: 'Pending' },
              { id: 'verified', label: 'Verified' },
              { id: 'recovered', label: 'Recovered' },
              { id: 'lost', label: 'Lost' }
            ].map(f => (
              <button
                key={f.id}
                onClick={() => setFilter(f.id)}
                style={{
                  padding: '8px 14px',
                  borderRadius: 8,
                  border: 'none',
                  background: filter === f.id ? (STATUS_CONFIG[f.id]?.color || '#1565C0') : 'transparent',
                  color: filter === f.id ? '#fff' : (isDark ? '#94a3b8' : '#64748b'),
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <button
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 14px',
                borderRadius: 10,
                border: `1px solid ${hasActiveAdvancedFilters ? '#1E88E5' : (isDark ? '#334155' : '#e5e7eb')}`,
                background: hasActiveAdvancedFilters ? 'rgba(30, 136, 229, 0.1)' : (isDark ? '#1e293b' : '#fff'),
                color: hasActiveAdvancedFilters ? '#1E88E5' : (isDark ? '#f8fafc' : '#1f2937'),
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              <SlidersIcon />
              Advanced
              {hasActiveAdvancedFilters && (
                <span style={{
                  width: 18, height: 18, borderRadius: 9,
                  background: '#1E88E5', color: '#fff',
                  fontSize: 11, fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {[positionStatus !== 'all', activityStage !== 'all', !!dateFrom, !!dateTo].filter(Boolean).length}
                </span>
              )}
            </button>
            <div style={{ position: 'relative' }}>
              <Icons.Search style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
              <input
                type="text"
                placeholder="Search opportunities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  padding: '10px 14px 10px 40px',
                  borderRadius: 10,
                  border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                  background: isDark ? '#1e293b' : '#fff',
                  color: isDark ? '#f8fafc' : '#1f2937',
                  fontSize: 14,
                  width: 260
                }}
              />
            </div>
          </div>
        </div>

        {/* Advanced Filters Panel */}
        {showAdvancedFilters && (
          <div
            className="fr-card-animate"
            style={{
              background: isDark ? '#1e293b' : '#fff',
              borderRadius: 14,
              border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
              padding: 20,
              marginTop: 12
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Icons.Filter style={{ color: '#1E88E5' }} /> Advanced Filters
              </h4>
              {hasActiveAdvancedFilters && (
                <button
                  onClick={clearAdvancedFilters}
                  style={{
                    padding: '6px 12px', borderRadius: 6,
                    border: 'none', background: 'rgba(239, 68, 68, 0.1)',
                    color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer'
                  }}
                >
                  Clear All
                </button>
              )}
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
              {/* Position Status Filter */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
                  Position Status
                </label>
                <select
                  value={positionStatus}
                  onChange={(e) => setPositionStatus(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                    background: isDark ? '#0f172a' : '#fff',
                    color: isDark ? '#f8fafc' : '#1f2937',
                    fontSize: 13, cursor: 'pointer'
                  }}
                >
                  {POSITION_STATUS_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Activity Stage Filter */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase' }}>
                  Activity Stage (Min)
                </label>
                <select
                  value={activityStage}
                  onChange={(e) => setActivityStage(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                    background: isDark ? '#0f172a' : '#fff',
                    color: isDark ? '#f8fafc' : '#1f2937',
                    fontSize: 13, cursor: 'pointer'
                  }}
                >
                  {ACTIVITY_STAGES.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
                <p style={{ margin: '6px 0 0', fontSize: 11, color: '#64748b' }}>Filter by submit or beyond</p>
              </div>

              {/* Date From */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CalendarIcon /> Activity From
                </label>
                <input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                    background: isDark ? '#0f172a' : '#fff',
                    color: isDark ? '#f8fafc' : '#1f2937',
                    fontSize: 13, cursor: 'pointer'
                  }}
                />
              </div>

              {/* Date To */}
              <div>
                <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 8, textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <CalendarIcon /> Activity To
                </label>
                <input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px', borderRadius: 8,
                    border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
                    background: isDark ? '#0f172a' : '#fff',
                    color: isDark ? '#f8fafc' : '#1f2937',
                    fontSize: 13, cursor: 'pointer'
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 16, padding: 12, background: isDark ? '#0f172a' : '#f8fafc', borderRadius: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <Icons.AlertCircle style={{ color: '#f59e0b', flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: '#64748b' }}>
                Use these filters to find candidates who reached "Submit" stage or beyond within a specific date range, for positions with a particular status.
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Opportunities List */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {filteredOpportunities.length === 0 ? (
          <div style={{
            background: isDark ? '#1e293b' : '#fff',
            borderRadius: 16,
            border: `1px solid ${isDark ? '#334155' : '#e5e7eb'}`,
            padding: 60,
            textAlign: 'center'
          }}>
            <Icons.Target style={{ marginBottom: 12, color: '#64748b', opacity: 0.5 }} />
            <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>No opportunities found</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>
              {filter !== 'all' ? 'Try changing your filter' : 'Sync from your ATS to scan for fee recovery opportunities'}
            </div>
          </div>
        ) : filteredOpportunities.map((opp, idx) => (
          <OpportunityCard
            key={opp.id}
            opportunity={opp}
            isDark={isDark}
            onAction={handleAction}
            animationDelay={idx * 50}
          />
        ))}
      </div>

      {/* Recovery Modal */}
      {recoveryModal && (
        <RecoveryModal
          opportunity={recoveryModal}
          isDark={isDark}
          onClose={() => setRecoveryModal(null)}
          onSubmit={handleRecoverySubmit}
        />
      )}

      {/* kNine Branded Modal */}
      {knModal && (
        <>
          <div onClick={() => handleKnModalClose(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1200 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1201, width: 380, maxWidth: '90vw' }}>
            <div style={{ background: isDark ? '#1e293b' : '#fff', borderRadius: 16, padding: 20, border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                  background: `linear-gradient(135deg, ${knModal.variant === 'success' ? '#10b981' : knModal.variant === 'error' ? '#ef4444' : '#1E88E5'}, #1565C0)`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 16, fontWeight: 700
                }}>K9</div>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: isDark ? '#f8fafc' : '#0f172a' }}>{knModal.title}</h3>
                  <p style={{ margin: '6px 0 0', fontSize: 13, color: isDark ? '#94a3b8' : '#64748b', lineHeight: 1.5 }}>{knModal.message}</p>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => handleKnModalClose(true)} style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #1E88E5, #1565C0)', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>OK</button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
